/* eslint-disable max-len */
/* eslint-disable no-unused-vars */

import { useEffect, useRef } from "react";

import { AppointmentFormSchemaType } from "../../state/createAppointmentData/createAppointment.reducer";
import { DASHBOARD } from "../dashboard/dashboard.query";
import { GET_APPOINTMENTS } from "../appointment-list/appointment-list.query";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";

import { toastMessageActions } from "../../state/request";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useDispatch } from "react-redux";
import { useMutation } from "@apollo/client";
import { useBookingAppointmentTypeStore } from "../../hooks";
import { ASSIGNED_BOOKING_TYPE, AssignedBookingType, CREATE_BOOKING_TYPE } from "../../config/params";
import { ADD_APPOINTMENT_V2 } from "./create-appointment-v2.mutation";
import { mapToAppointmentCreateRequest } from "./create-appointment-v2.mapper";
import { IAppointmentCreateRequest, IUpdatedCreateAppointmentResponse } from "./create-appointment-v2.interface";
import { userActivityLogger } from "../../utils/mixpanel";

const mapToGenericAppointmentResponse = (data: IUpdatedCreateAppointmentResponse)=>{
  const allResources = data.appointments.map((appointment) => appointment.resources[0]);

  const seenIds = new Set();

  const combinedResources = allResources.filter((resource) => {
    if (!resource || seenIds.has(resource.id)) {
      return false;
    }
    seenIds.add(resource.id);

    return true;
  }); const updatedPetList = data.appointments.map((appointment) => ({
    ...appointment.pet,
    appointmentCreateType: appointment.appointmentCreateType,
  }));

  let appointmentCreateType: AssignedBookingType = ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING;

  if (data.appointments.some((a) => a.appointmentCreateType === ASSIGNED_BOOKING_TYPE.NORMAL_BOOKING)) {
    appointmentCreateType = ASSIGNED_BOOKING_TYPE.NORMAL_BOOKING;
  } else if (data.appointments.some((a) => a.appointmentCreateType === ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING)) {
    appointmentCreateType = ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING;
  }

  return {
    start_at: data.appointments[0].start_at,
    end_at: data.appointments[0].end_at,
    resources: combinedResources,
    duration: data.appointments[0].duration,
    appointmentCreateType,
    promoCode: data.appointments[0].promoCode,
    slotUnavailable: data.slotUnavailable,
    pets: updatedPetList,
    location: data.location,
    success: data.success,
    messages: data.messages,
  };
};

export const useAppointmentCreateV2 = (newUserBooking: boolean = false) => {
  const dispatch = useDispatch();
  const flow = useRef<any>();
  const { appointmentTypeIndicator, bookingAppointmentType } = useBookingAppointmentTypeStore();
  const { appointmentForm,
    reasonForVisit, setAppointmentResponse, selectedPets, promoCode } = useAppointmentStore();

  const [addAppointment, { data, loading, error }] =
    useMutation<{ addAppointmentV2: IUpdatedCreateAppointmentResponse }>(ADD_APPOINTMENT_V2);


  const createAppointmentV2 = (userFlow=false) => {
    const appointmentRequest: IAppointmentCreateRequest =
      mapToAppointmentCreateRequest(appointmentForm as AppointmentFormSchemaType,
        reasonForVisit, appointmentTypeIndicator, promoCode);

    flow.current=userFlow;
    addAppointment({
      variables: { data: appointmentRequest },
      refetchQueries: [
        { query: GET_APPOINTMENTS },
        { query: DASHBOARD },
      ],
    }).catch((error) => {
      {appointmentTypeIndicator !== ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING &&
        userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT_FAILURE, {
          appointment_category: getAppointmentCategory(appointmentTypeIndicator),
          date: appointmentForm&& appointmentForm?.day,
          time_slot: appointmentForm&& appointmentForm?.selectedSlotLabel,
          no_of_pets: selectedPets.length,
          owner_type: "single_pet",
          user_type_new: flow.current ? "yes" : "no",
          error: error.message,
          appt_type: bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ? "drop_off" : "in_room_visit",
        });}
    });
  };

  const getAppointmentTypeText = (appointmentTypeIndicator: Number) =>{
    switch (appointmentTypeIndicator) {
    case ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING:
      return "drop_off";
    case ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING:
      return "express_visit";
    case ASSIGNED_BOOKING_TYPE.NORMAL_BOOKING:
      return "in_room_visit";
    }
  };

  const getAppointmentCategory = (type: Number) => {
    switch (type) {
    case ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING:
      return "drop_off";
    case ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING:
      return "tech";
    default:
      return "dvm";
    }
  };

  useEffect(() => {
    if (data) {
      if (data.addAppointmentV2.slotUnavailable) {
        userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT_FAILURE, {
          appointment_category: getAppointmentCategory(appointmentTypeIndicator),
          date: appointmentForm&& appointmentForm?.day,
          time_slot: appointmentForm&& appointmentForm?.selectedSlotLabel,
          no_of_pets: selectedPets.length,
          owner_type: "single_pet",
          user_type_new: flow.current ? "yes" : "no",
          error: "Booked time slot is not available",
          appt_type: bookingAppointmentType === CREATE_BOOKING_TYPE.DROPOFF_BOOKING ? "drop_off" : "in_room_visit",
        });

        return;
      }
      const modifiedData = mapToGenericAppointmentResponse(data.addAppointmentV2);

      if (appointmentTypeIndicator !== ASSIGNED_BOOKING_TYPE.EXPRESS_BOOKING) {
        modifiedData.pets.forEach((pet) => {
          userActivityLogger(MIXPANEL_EVENTS.BOOK_A_VISIT_SUCCESS, {
            appointment_category: getAppointmentCategory(pet.appointmentCreateType),
            date: appointmentForm&& appointmentForm?.day,
            time_slot: appointmentForm&& appointmentForm?.selectedSlotLabel,
            owner_type: modifiedData.pets?.length==1?"single_pet":"multipet",
            user_type_new: newUserBooking ? "yes" : "no",
            pet_name: pet.name,
            is_promo_used: promoCode && promoCode?.length > 0 ? true : false,
            promocode: promoCode && promoCode?.join(", "),
            appt_type: getAppointmentTypeText(pet.appointmentCreateType),
          });
        });
      }
      if (data?.addAppointmentV2)setAppointmentResponse(modifiedData);
    }
  }, [data]);

  useEffect(() => {
    dispatch(toastMessageActions.setGlobalLoading(loading));
  }, [loading]);

  return {
    createAppointmentV2,
    appointmentCreatedV2: data?.addAppointmentV2,
    success: data?.addAppointmentV2?.success,
    errorMessage: data?.addAppointmentV2?.messages,
  };
};
